/**
 * FileUpload - 파일 업로드 컴포넌트
 *
 * 드래그 앤 드롭 또는 파일 선택 대화상자를 통한 파일 업로드를 지원.
 * @param variant - 'dropzone' (기본) | 'button' | 'icon' 형태
 * @param maxSize - 파일 최대 크기 (바이트), 초과 시 에러 메시지 표시
 * @param uploadFiles - 외부에서 관리하는 업로드 상태 (진행률, 완료, 에러)
 */
'use client';

import React, { useState, useRef, useCallback } from 'react';
import {
  CloudUpload,
  File as FileIcon,
  X,
  Trash2,
  Download,
  CheckCircle,
  XCircle,
  RotateCcw,
} from 'lucide-react';
import styles from './FileUpload.module.css';

export type FileStatus = 'uploading' | 'complete' | 'error';

export interface UploadFile {
  file: File;
  status?: FileStatus;
  progress?: number;
  errorMessage?: string;
}

export interface FileUploadProps {
  onUpload: (files: File[]) => void;
  onRetry?: (file: File) => void;
  accept?: string;
  maxSize?: number;
  multiple?: boolean;
  variant?: 'dropzone' | 'button' | 'icon';
  label?: string;
  description?: string;
  error?: string;
  uploadFiles?: UploadFile[];
  disabled?: boolean;
  className?: string;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

export default function FileUpload({
  onUpload,
  onRetry,
  accept,
  maxSize,
  multiple = false,
  variant = 'dropzone',
  label,
  description,
  error: externalError,
  uploadFiles,
  disabled = false,
  className = '',
}: FileUploadProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [sizeError, setSizeError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const processFiles = useCallback(
    (incoming: FileList | File[]) => {
      const fileArray = Array.from(incoming);
      const oversized: string[] = [];
      const valid: File[] = [];

      fileArray.forEach((f) => {
        if (maxSize && f.size > maxSize) {
          oversized.push(f.name);
        } else {
          valid.push(f);
        }
      });

      if (oversized.length > 0) {
        setSizeError(`파일 크기 초과: ${oversized.join(', ')} (최대 ${formatFileSize(maxSize!)})`);
      } else {
        setSizeError(null);
      }

      if (valid.length > 0) {
        setFiles((prev) => {
          const next = multiple ? [...prev, ...valid] : valid.slice(0, 1);
          onUpload(next);
          return next;
        });
      }
    },
    [maxSize, multiple, onUpload],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (disabled) return;
      processFiles(e.dataTransfer.files);
    },
    [disabled, processFiles],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!disabled) setDragOver(true);
    },
    [disabled],
  );

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        processFiles(e.target.files);
      }
      e.target.value = '';
    },
    [processFiles],
  );

  const removeFile = useCallback(
    (index: number) => {
      const next = files.filter((_, i) => i !== index);
      setFiles(next);
      onUpload(next);
      setSizeError(null);
    },
    [files, onUpload],
  );

  const removeAll = useCallback(() => {
    setFiles([]);
    onUpload([]);
    setSizeError(null);
  }, [onUpload]);

  const displayError = externalError || sizeError;

  const hiddenInput = (
    <input
      ref={inputRef}
      type="file"
      className={styles.hiddenInput}
      accept={accept}
      multiple={multiple}
      onChange={handleChange}
      disabled={disabled}
      tabIndex={-1}
    />
  );

  // Button variant
  if (variant === 'button') {
    return (
      <div className={[styles.container, className].filter(Boolean).join(' ')}>
        {label && <label className={styles.label}>{label}</label>}
        {hiddenInput}
        <button
          type="button"
          className={`${styles.browseButton} ${disabled ? styles.disabled : ''}`}
          onClick={() => !disabled && inputRef.current?.click()}
          disabled={disabled}
        >
          <Download size={16} />
          파일 선택
        </button>
        {description && <span className={styles.dropzoneDescription}>{description}</span>}
        {displayError && (
          <p className={styles.errorText} role="alert">
            {displayError}
          </p>
        )}
        {renderFileList()}
      </div>
    );
  }

  // Icon variant
  if (variant === 'icon') {
    return (
      <div className={[styles.container, className].filter(Boolean).join(' ')}>
        {label && <label className={styles.label}>{label}</label>}
        {hiddenInput}
        <div
          className={`${styles.iconDropzone} ${dragOver ? styles.dragOver : ''} ${disabled ? styles.disabled : ''} ${displayError ? styles.dropzoneError : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => !disabled && inputRef.current?.click()}
          role="button"
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
          aria-label={label || '파일 업로드'}
        >
          <FileIcon size={28} className={styles.uploadIcon} />
          <span className={styles.iconText}>
            <strong>여기를 클릭</strong>하여 파일을 업로드하거나 드래그하세요.
          </span>
          {description && <span className={styles.dropzoneDescription}>{description}</span>}
        </div>
        {displayError && (
          <p className={styles.errorText} role="alert">
            {displayError}
          </p>
        )}
        {renderFileList()}
      </div>
    );
  }

  // Dropzone variant (default)
  function renderFileList() {
    // If uploadFiles provided (external status tracking), render them
    if (uploadFiles && uploadFiles.length > 0) {
      return (
        <div className={styles.fileList}>
          {uploadFiles.map((uf, index) => (
            <div key={`${uf.file.name}-${index}`} className={styles.fileItem}>
              <div className={styles.fileStatusIcon}>
                {uf.status === 'complete' ? (
                  <CheckCircle size={20} className={styles.successIcon} />
                ) : uf.status === 'error' ? (
                  <XCircle size={20} className={styles.errorIcon} />
                ) : (
                  <FileIcon size={20} className={styles.fileIcon} />
                )}
              </div>
              <div className={styles.fileInfo}>
                <div className={styles.fileNameRow}>
                  <span className={styles.fileName}>{uf.file.name}</span>
                  {uf.status !== 'complete' && (
                    <button
                      type="button"
                      className={styles.removeButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(index);
                      }}
                      aria-label={`${uf.file.name} 삭제`}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                  {uf.status === 'complete' && (
                    <CheckCircle size={18} className={styles.successBadge} />
                  )}
                </div>
                {uf.status === 'uploading' && (
                  <div className={styles.progressTrack}>
                    <div
                      className={styles.progressFill}
                      style={{ width: `${uf.progress ?? 0}%` }}
                    />
                  </div>
                )}
                {uf.status === 'complete' && (
                  <div className={styles.progressTrack}>
                    <div
                      className={`${styles.progressFill} ${styles.progressComplete}`}
                      style={{ width: '100%' }}
                    />
                  </div>
                )}
                {uf.status === 'error' && (
                  <div className={styles.progressTrack}>
                    <div
                      className={`${styles.progressFill} ${styles.progressError}`}
                      style={{ width: '60%' }}
                    />
                  </div>
                )}
                <div className={styles.fileMeta}>
                  {uf.status === 'uploading' && (
                    <>
                      <span className={styles.fileSize}>{formatFileSize(uf.file.size)}</span>
                      <span className={styles.fileProgress}>{uf.progress ?? 0}%</span>
                    </>
                  )}
                  {uf.status === 'complete' && (
                    <>
                      <span className={styles.successText}>업로드 완료!</span>
                      <span className={styles.fileProgress}>100%</span>
                    </>
                  )}
                  {uf.status === 'error' && (
                    <>
                      <span className={styles.errorMessage}>
                        {uf.errorMessage || '업로드 실패. 다시 시도해주세요.'}
                      </span>
                      {onRetry && (
                        <button
                          type="button"
                          className={styles.retryButton}
                          onClick={() => onRetry(uf.file)}
                        >
                          다시 시도 <RotateCcw size={12} />
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    // Default simple file list
    if (files.length === 0) return null;
    return (
      <div className={styles.fileList}>
        <div className={styles.fileListHeader}>
          <span className={styles.fileCount}>{files.length}개 파일</span>
          <button type="button" className={styles.clearAll} onClick={removeAll}>
            <Trash2 size={14} />
            전체 삭제
          </button>
        </div>
        {files.map((file, index) => (
          <div key={`${file.name}-${index}`} className={styles.fileItem}>
            <FileIcon size={18} className={styles.fileIcon} />
            <div className={styles.fileInfo}>
              <span className={styles.fileName}>{file.name}</span>
              <span className={styles.fileSize}>{formatFileSize(file.size)}</span>
            </div>
            <button
              type="button"
              className={styles.removeButton}
              onClick={(e) => {
                e.stopPropagation();
                removeFile(index);
              }}
              aria-label={`${file.name} 삭제`}
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={[styles.container, className].filter(Boolean).join(' ')}>
      {label && <label className={styles.label}>{label}</label>}
      {hiddenInput}
      <div
        className={[
          styles.dropzone,
          dragOver ? styles.dragOver : '',
          disabled ? styles.disabled : '',
          displayError ? styles.dropzoneError : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !disabled && inputRef.current?.click()}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        aria-label={label || '파일 업로드'}
      >
        <span className={styles.dropzoneText}>파일을 드래그하거나 클릭하여 업로드</span>
        {description && <span className={styles.dropzoneDescription}>{description}</span>}
        <button
          type="button"
          className={styles.browseButton}
          onClick={(e) => {
            e.stopPropagation();
            inputRef.current?.click();
          }}
          disabled={disabled}
        >
          파일 선택 <Download size={14} />
        </button>
      </div>

      {displayError && (
        <p className={styles.errorText} role="alert">
          {displayError}
        </p>
      )}

      {renderFileList()}
    </div>
  );
}
